/**
 * Visx Bar implementation. SVG-native, fully DLS-token-driven.
 *
 * Uses scaleBand for the categorical axis and scaleLinear for the value axis.
 * Hover state managed locally; tooltip rendered via DrillAwareTooltip
 * positioned with @visx/tooltip.
 */

import { useMemo } from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { Bar } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { scaleBand, scaleLinear } from '@visx/scale';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import type { BarChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = BarChartProps & CommonChartProps;

const MARGIN = { top: 12, right: 16, bottom: 36, left: 48 };

export function VisxBar(props: Props): JSX.Element {
  return (
    <ParentSize>
      {({ width, height }) =>
        width > 0 && height > 0 ? <VisxBarInner {...props} width={width} height={height} /> : null
      }
    </ParentSize>
  );
}

function VisxBarInner({
  data,
  width,
  height,
}: Props & { width: number; height: number }): JSX.Element {
  const theme = chartTheme('visx').theme;
  const palette = theme.categoricalPalette;
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } =
    useTooltip<{ label: string; value: number; index: number }>();

  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: data.map((d) => d.label),
        range: [0, innerW],
        padding: 0.25,
      }),
    [data, innerW]
  );

  const yMax = useMemo(() => Math.max(0, ...data.map((d) => d.value)), [data]);
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, yMax || 1],
        range: [innerH, 0],
        nice: true,
      }),
    [yMax, innerH]
  );

  return (
    <div style={{ position: 'relative', width, height }}>
      <svg width={width} height={height}>
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridRows scale={yScale} width={innerW} stroke={theme.gridColor} strokeDasharray="3 3" />
          {data.map((d, i) => {
            const x = xScale(d.label) ?? 0;
            const barW = xScale.bandwidth();
            const y = yScale(d.value);
            const barH = innerH - y;
            const fill = (d.color as string) ?? palette[i % palette.length];
            return (
              <Bar
                key={`${d.label}-${i}`}
                x={x}
                y={y}
                width={barW}
                height={barH}
                fill={fill}
                rx={4}
                onMouseMove={(event) => {
                  const target = event.currentTarget as SVGRectElement;
                  const rect = target.ownerSVGElement?.getBoundingClientRect();
                  const cursorX = event.clientX - (rect?.left ?? 0);
                  const cursorY = event.clientY - (rect?.top ?? 0);
                  showTooltip({
                    tooltipLeft: cursorX,
                    tooltipTop: cursorY,
                    tooltipData: { label: d.label, value: d.value, index: i },
                  });
                }}
                onMouseLeave={hideTooltip}
              />
            );
          })}
          <AxisBottom top={innerH} scale={xScale} stroke={theme.gridColor} tickStroke={theme.gridColor} tickLabelProps={{ fill: theme.textColor, fontSize: 11, textAnchor: 'middle' }} />
          <AxisLeft scale={yScale} stroke={theme.gridColor} tickStroke={theme.gridColor} tickLabelProps={{ fill: theme.textColor, fontSize: 11, textAnchor: 'end', dx: -4 }} numTicks={5} />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={{ ...defaultStyles, background: 'transparent', padding: 0, boxShadow: 'none' }}>
          <DrillAwareTooltip
            rows={[
              {
                color: (data[tooltipData.index].color as string) ?? palette[tooltipData.index % palette.length],
                label: tooltipData.label,
                value: tooltipData.value,
              },
            ]}
          />
        </TooltipWithBounds>
      )}
    </div>
  );
}
